/**
 * Strategies Hub
 *
 * Landing page for the Strategy section. Shows all three strategy frameworks
 * available in the platform, their object models, and provides navigation
 * to each. Replaces the redirect-to-alignment index route.
 *
 * Three frameworks (in order):
 *   1. Classic Strategy    — Strategy → Objective → Goal → Work Item
 *   2. Opportunity Solution Tree — Desired Outcome → Opportunity → Solution → Experiment
 *   3. Kernel Strategy     — Diagnosis → Guiding Policy → Coherent Action
 *
 * Feature flags:
 *   - Classic Strategy always available (governed by parent STRATEGY_NAV)
 *   - OST always available (governed by parent STRATEGY_NAV)
 *   - Kernel Strategy gated by FEATURE_FLAGS.STRATEGY_KERNEL_NAV
 */

import { useMemo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import {
  Target,
  Lightbulb,
  Wrench,
  FlaskConical,
  ChevronRight,
  ArrowRight,
  TreeDeciduous,
  Flag,
  BookOpen,
  Layers,
  Crosshair,
  Sparkles,
  LayoutTemplate,
  AlignVerticalJustifyStart,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useOSTStore } from '../../../stores/prioritization/ostStore';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useStrategyKernelStore } from '../../../stores/prioritization/strategyKernelStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useFeatureFlag, FEATURE_FLAGS } from '../../../lib/featureFlags';
import {
  getWorkspaceModules,
  isModuleEnabled,
  WORKSPACE_MODULES_UPDATED_EVENT,
} from '../../../lib/workspaceModulesService';
import { useStrategyCanvasStore } from '../../../stores/prioritization/strategyCanvasStore';
import { useV2momStore } from '../../../stores/prioritization/v2momStore';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';

// ============================================================================
// Object chain component
// ============================================================================

interface ChainNode {
  label: string;
  icon: React.ElementType;
  color: string;
  bg: string;
}

function ObjectChain({ nodes }: { nodes: ChainNode[] }) {
  return (
    <div className="flex items-center flex-wrap gap-1.5 mt-3">
      {nodes.map((node, i) => {
        const Icon = node.icon;
        return (
          <div key={node.label} className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1 px-2 py-1  text-xs font-medium ${node.bg} ${node.color}`}
            >
              <Icon className="h-3 w-3" />
              {node.label}
            </span>
            {i < nodes.length - 1 && (
              <ChevronRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Recommendations — OST trees with solutions ready to prioritize
// ============================================================================

function OSTRecommendations() {
  const trees = useOSTStore(state => state.trees);
  const nodes = useOSTStore(state => state.nodes);
  const navigate = useNavigate();

  const readyTrees = useMemo(() => {
    return trees
      .filter(tree => {
        const treeNodes = nodes.filter(n => n.treeId === tree.id);
        const hasOutcome = treeNodes.some(n => n.nodeType === 'outcome');
        const hasOpportunity = treeNodes.some(n => n.nodeType === 'opportunity');
        const hasSolution = treeNodes.some(n => n.nodeType === 'solution');
        return hasOutcome && hasOpportunity && hasSolution;
      })
      .slice(0, 3);
  }, [trees, nodes]);

  if (readyTrees.length === 0) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[var(--mustard)]" />
        <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
          Ready for prioritization
        </h2>
      </div>
      <p className="text-sm text-[var(--text-ter)] mb-4">
        These trees have a complete outcome → opportunity → solution chain and
        can be imported into a prioritization cycle.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {readyTrees.map(tree => {
          const treeNodes = nodes.filter(n => n.treeId === tree.id);
          const solutionCount = treeNodes.filter(n => n.nodeType === 'solution').length;
          const opportunityCount = treeNodes.filter(n => n.nodeType === 'opportunity').length;
          return (
            <button
              key={tree.id}
              onClick={() => navigate(`/prioritization/strategy/opportunity-tree/${tree.id}`)}
              className="text-left p-4 border border-primary-200 bg-primary-50 hover:bg-primary-100 hover:border-primary-300 dark:hover:bg-[var(--surface-raised)] dark:hover:border-[var(--rule)] transition-all group"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--text-pri)] truncate group-hover:text-primary-700 dark:group-hover:text-primary-400">
                    {tree.name}
                  </p>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-[var(--text-ter)]">
                    <span>{opportunityCount} opportunit{opportunityCount !== 1 ? 'ies' : 'y'}</span>
                    <span>{solutionCount} solution{solutionCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-primary-400 group-hover:text-primary-600 dark:text-primary-500 dark:group-hover:text-primary-300 flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Strategy type card
// ============================================================================

interface StrategyCardProps {
  accent: 'violet' | 'primary' | 'amber' | 'indigo';
  icon: React.ElementType;
  name: string;
  tagline: string;
  description: string;
  objectChain: ChainNode[];
  count: number | null;
  countLabel: string;
  listHref: string;
  newHref?: string;
  newLabel?: string;
  enabled: boolean;
  archivedCount?: number;
  archivedHref?: string;
}

const ACCENT_STYLES = {
  violet: {
    iconBg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/35',
    iconColor: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    border: 'border-[var(--slate)] dark:border-[var(--slate)]/80',
    hoverBorder: 'hover:border-[var(--slate)] dark:hover:border-[var(--slate)]',
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50',
    btn: 'border border-[var(--slate)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:border-[var(--slate)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--slate)]/40',
    divider: 'divide-violet-100',
  },
  primary: {
    iconBg: 'bg-primary-50 dark:bg-primary-950/35',
    iconColor: 'text-primary-600 dark:text-primary-400',
    border: 'border-primary-200 dark:border-primary-900/60',
    hoverBorder: 'hover:border-primary-300 dark:hover:border-primary-700',
    badge: 'bg-primary-100 text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
    btn: 'border border-primary-300 text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-950/40',
    divider: 'divide-primary-100',
  },
  amber: {
    iconBg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/35',
    iconColor: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    border: 'border-[var(--mustard)] dark:border-[var(--mustard)]/60',
    hoverBorder: 'hover:border-[var(--mustard)] dark:hover:border-[var(--mustard)]',
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/50 dark:text-[var(--mustard)]',
    btn: 'border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:border-[var(--mustard)] dark:text-[var(--mustard)] dark:hover:bg-[var(--mustard)]/40',
    divider: 'divide-amber-100',
  },
  indigo: {
    iconBg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/35',
    iconColor: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    border: 'border-[var(--slate)] dark:border-[var(--slate)]/60',
    hoverBorder: 'hover:border-[var(--slate)] dark:hover:border-[var(--slate)]',
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50',
    btn: 'border border-[var(--slate)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:border-[var(--slate)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--slate)]/40',
    divider: 'divide-indigo-100',
  },
};

function StrategyTypeCard({
  accent,
  icon: Icon,
  name,
  tagline,
  description,
  objectChain,
  count,
  countLabel,
  listHref,
  newHref,
  newLabel = 'New',
  enabled,
  archivedCount,
  archivedHref,
}: StrategyCardProps) {
  const s = ACCENT_STYLES[accent];

  return (
    <div
      className={`relative flex flex-col  border p-6 bg-[var(--bg-raised)] dark:bg-[var(--surface)] ${
        enabled ? `${s.border} ${s.hoverBorder}` : 'border-[var(--rule)]'
      } transition-all`}
    >
      {/* Icon + name */}
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10  ${enabled ? s.iconBg : 'bg-[var(--surface)] dark:bg-[var(--bg-raised)]/60'} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${enabled ? s.iconColor : 'text-[var(--text-ter)]'}`} />
        </div>
        <div className="min-w-0">
          <h3 className={`font-semibold text-base ${enabled ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
            {name}
          </h3>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{tagline}</p>
        </div>
      </div>

      {/* Description */}
      <p className={`mt-4 text-sm leading-relaxed ${enabled ? 'text-[var(--text-sec)] dark:text-[var(--text-sec)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
        {description}
      </p>

      {/* Object chain */}
      <div className="mt-4">
        <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-1">Object model</p>
        <ObjectChain nodes={objectChain} />
      </div>

      {/* Spacer to push footer to bottom */}
      <div className="flex-1" />

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-[var(--rule)] dark:border-[var(--rule)]">
        {enabled && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.badge}`}>
                {count === null ? '—' : count} {countLabel}
              </span>
              <div className="flex items-center gap-2">
                {newHref && (
                  <Link
                    to={newHref}
                    className={`inline-flex items-center gap-1 px-3 py-1.5  text-xs font-medium transition-colors ${s.btn}`}
                  >
                    {newLabel}
                  </Link>
                )}
                <Link
                  to={listHref}
                  className={`inline-flex items-center gap-1 px-3 py-1.5  text-xs font-medium transition-colors ${s.btn}`}
                >
                  View all
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            {archivedCount != null && archivedCount > 0 && archivedHref && (
              <div className="text-right">
                <Link
                  to={archivedHref}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                >
                  {archivedCount} archived
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Unified all-strategies table
// ============================================================================

interface AnyStrategyRow {
  id: string;
  name: string;
  type: 'Classic' | 'OST' | 'Kernel';
  status: string;
  timeHorizon: string;
  href: string;
}

const TYPE_BADGE: Record<string, string> = {
  Classic:
    'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/55',
  OST: 'bg-primary-100 text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
  Kernel: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/55 dark:text-[var(--mustard)]',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/45 dark:text-[var(--text-ter)]',
  draft: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  archived: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/45 dark:text-[var(--coral)]',
};

function AllStrategiesTable({ rows }: { rows: AnyStrategyRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-3">All Strategies</h2>
      <div className="overflow-hidden border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        <table className="min-w-full divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          <thead>
            <tr className="bg-[var(--surface)] dark:bg-[var(--bg-raised)]/80">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Name</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Type</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Status</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide hidden sm:table-cell">Time Horizon</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {rows.map(row => (
              <tr key={`${row.type}-${row.id}`} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/50 transition-colors">
                <td className="px-4 py-3">
                  <Link
                    to={row.href}
                    className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
                  >
                    {row.name}
                  </Link>
                  {row.timeHorizon && (
                    <span className="sm:hidden block mt-0.5 text-xs text-[var(--text-ter)]">{row.timeHorizon}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[row.type] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                    {row.type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {row.status ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[row.status] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                      {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                    </span>
                  ) : <span className="text-xs text-[var(--text-ter)]">—</span>}
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span className="text-xs text-[var(--text-ter)]">{row.timeHorizon || '—'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

export function StrategiesHub() {
  // Classic Strategy routes are always live (no flag gate on routes).
  // Kernel Strategy is gated by BOTH the feature flag AND the workspace module.
  const kernelFlagEnabled = useFeatureFlag(FEATURE_FLAGS.STRATEGY_KERNEL_NAV);
  const canvasFlagEnabled = useFeatureFlag(FEATURE_FLAGS.STRATEGY_CANVAS_NAV);
  const v2momFlagEnabled = useFeatureFlag(FEATURE_FLAGS.V2MOM_NAV);
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  const kernelEnabled = kernelFlagEnabled && isModuleEnabled(workspaceFeatures, 'strategy_kernel');
  const canvasEnabled = canvasFlagEnabled && isModuleEnabled(workspaceFeatures, 'strategy_canvas');
  const v2momEnabled = v2momFlagEnabled && isModuleEnabled(workspaceFeatures, 'v2mom');

  const workspaceId = useTenantStore(state => state.currentWorkspaceId);
  const fetchOSTTrees = useOSTStore(state => state.fetchTrees);
  const fetchKernels = useStrategyKernelStore(state => state.fetchFromSupabase);

  const fetchCanvases = useStrategyCanvasStore((s) => s.fetchCanvases);
  const canvases      = useStrategyCanvasStore((s) => s.canvases);
  const fetchV2moms   = useV2momStore((s) => s.fetchV2moms);
  const v2moms        = useV2momStore((s) => s.v2moms);

  useEffect(() => {
    fetchOSTTrees(workspaceId ?? undefined);
    fetchKernels();
    if (workspaceId && canvasEnabled) fetchCanvases();
    if (workspaceId && v2momEnabled) void fetchV2moms();
  }, [fetchOSTTrees, fetchKernels, fetchCanvases, fetchV2moms, workspaceId, canvasEnabled, v2momEnabled]);

  // Keep workspace module state in sync (same pattern as Sidebar).
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (!workspaceId) {
        if (!cancelled) setWorkspaceFeatures({});
        return;
      }
      getWorkspaceModules(workspaceId).then(({ data }) => {
        if (!cancelled) setWorkspaceFeatures(data);
      });
    };
    load();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, load);
    };
  }, [workspaceId]);

  // Counts from stores — exclude archived from active badge
  const strategiesMap = useStrategyStore(state => state.strategies);
  const { classicCount, classicArchivedCount } = useMemo(() => {
    const all = strategiesMap instanceof Map
      ? Array.from(strategiesMap.values())
      : Object.values(strategiesMap ?? {});
    return {
      classicCount: all.filter(s => s.status !== 'archived').length,
      classicArchivedCount: all.filter(s => s.status === 'archived').length,
    };
  }, [strategiesMap]);

  const ostTrees = useOSTStore(state => state.trees);
  const ostCount = ostTrees.length;

  const kernels = useStrategyKernelStore(state => state.kernels);
  const kernelCount = useMemo(() => {
    if (!kernels) return 0;
    if (kernels instanceof Map) return kernels.size;
    if (Array.isArray(kernels)) return kernels.length;
    return Object.keys(kernels).length;
  }, [kernels]);

  // Unified all-strategies rows
  const allRows = useMemo((): AnyStrategyRow[] => {
    const classicRows: AnyStrategyRow[] = (
      strategiesMap instanceof Map
        ? Array.from(strategiesMap.values())
        : Object.values(strategiesMap ?? {})
    ).map(s => ({
      id: s.id,
      name: s.name,
      type: 'Classic' as const,
      status: s.status,
      timeHorizon: s.timeHorizon ?? '',
      href: `/prioritization/strategy/${s.id}`,
    }));

    const ostRows: AnyStrategyRow[] = ostTrees.map(t => ({
      id: t.id,
      name: t.name,
      type: 'OST' as const,
      status: '',
      timeHorizon: '',
      href: `/prioritization/strategy/opportunity-tree/${t.id}`,
    }));

    const kernelArr = kernels instanceof Map
      ? Array.from(kernels.values())
      : Array.isArray(kernels) ? kernels : Object.values(kernels ?? {});
    const kernelRows: AnyStrategyRow[] = kernelArr.map((k: { id: string; name: string; status?: string; timeHorizon?: string }) => ({
      id: k.id,
      name: k.name,
      type: 'Kernel' as const,
      status: k.status ?? '',
      timeHorizon: k.timeHorizon ?? '',
      href: `/prioritization/strategy/kernel/${k.id}`,
    }));

    return [...classicRows, ...ostRows, ...(kernelEnabled ? kernelRows : [])];
  }, [strategiesMap, ostTrees, kernels, kernelEnabled]);

  // Object chain definitions
  const classicChain: ChainNode[] = [
    { label: 'Strategy', icon: ChartBarIcon, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Objective', icon: Flag, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Goal', icon: Crosshair, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Work Item', icon: Layers, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
  ];

  const ostChain: ChainNode[] = [
    { label: 'Desired Outcome', icon: Target, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Opportunity', icon: Lightbulb, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40' },
    { label: 'Solution', icon: Wrench, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Experiment', icon: FlaskConical, color: 'text-[var(--green)] dark:text-[var(--text-ter)]', bg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/40' },
  ];

  const kernelChain: ChainNode[] = [
    { label: 'Diagnosis', icon: BookOpen, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40' },
    { label: 'Guiding Policy', icon: Flag, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40' },
    { label: 'Coherent Action', icon: Layers, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40' },
  ];

  const v2momChain: ChainNode[] = [
    { label: 'Vision', icon: Sparkles, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Methods', icon: Layers, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Measures', icon: Flag, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
  ];

  const canvasChain: ChainNode[] = [
    { label: 'Drivers', icon: TreeDeciduous, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Vision/Purpose', icon: Sparkles, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
    { label: 'Strategy', icon: Layers, color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<TreeDeciduous className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
        iconBg="bg-primary-50"
        title="Strategy"
        subtitle="Frameworks, clarity artifacts, and how they feed prioritization. Open How this connects for the methodology map, then pick a framework below."
        actions={<StrategyConnectivityHelpButton context="strategy_hub" />}
      />

      <div className="border border-primary-200 bg-primary-50 px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 dark:shadow-inner">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-pri)]">
            New to these objects?
          </p>
          <p className="text-sm text-[var(--text-sec)] mt-0.5 leading-snug max-w-2xl">
            Follow a step-by-step path from outcome driver through strategy, objectives, and goals — with the same methodology map you will see on each screen.
          </p>
        </div>
        <Link
          to="/prioritization/strategy/guided-setup"
          className="inline-flex items-center justify-center gap-2 shrink-0 bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 transition-colors"
        >
          Open guided setup
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Three-column strategy type grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* 1. Classic Strategy */}
        <StrategyTypeCard
          accent="violet"
          icon={ChartBarIcon}
          name="Classic Strategy"
          tagline="Vision-driven, top-down alignment"
          description="Define a strategy with vision, mission, north-star goal, and strategic drivers. Link strategies to objectives and goals, then cascade into prioritization cycles."
          objectChain={classicChain}
          count={classicCount}
          countLabel={classicCount === 1 ? 'strategy' : 'strategies'}
          listHref="/prioritization/strategy/list"
          newHref="/prioritization/strategy/new?framework=classic"
          newLabel="New Strategy"
          enabled={true}
          archivedCount={classicArchivedCount}
          archivedHref="/prioritization/strategy/list?status=archived"
        />

        {/* 2. Opportunity Solution Tree (middle) */}
        <StrategyTypeCard
          accent="primary"
          icon={Target}
          name="Opportunity Solution Tree"
          tagline="Continuous discovery, outcome-first"
          description="Start with a desired outcome, surface customer opportunities from interviews, explore solutions, and run experiments. Proven by Teresa Torres' Continuous Discovery Habits."
          objectChain={ostChain}
          count={ostCount}
          countLabel={ostCount === 1 ? 'tree' : 'trees'}
          newHref="/prioritization/strategy/new?framework=ost"
          newLabel="New Tree"
          enabled={true}
        />

        {/* 3. Kernel Strategy — hidden when workspace module or individual flag is disabled */}
        {kernelEnabled && (
          <StrategyTypeCard
            accent="amber"
            icon={LightBulbIcon}
            name="Kernel Strategy"
            tagline="Diagnosis-first, Rumelt's framework"
            description="Richard Rumelt's Good Strategy framework. Start with an honest diagnosis of the challenge, set a guiding policy that responds to it, and define coherent actions that reinforce each other."
            objectChain={kernelChain}
            count={kernelEnabled ? kernelCount : null}
            countLabel={kernelCount === 1 ? 'kernel' : 'kernels'}
            listHref="/prioritization/strategy/kernel"
            newHref="/prioritization/strategy/new?framework=kernel"
            newLabel="New Kernel"
            enabled={kernelEnabled}
          />
        )}

        {/* 4. Strategy Canvas — hidden when workspace module is disabled */}
        {canvasEnabled && (
          <StrategyTypeCard
            accent="indigo"
            icon={LayoutTemplate}
            name="Strategy Canvas"
            tagline="One-page strategy communication"
            description="A one-page framework that ties Outcome Drivers, Vision, Purpose, Mission, Guiding Principles, and Competencies together with live Objectives and Goals — making complex change legible to everyone."
            objectChain={canvasChain}
            count={canvases.length}
            countLabel={canvases.length === 1 ? 'canvas' : 'canvases'}
            listHref="/prioritization/strategy/canvas"
            newHref="/prioritization/strategy/canvas"
            newLabel="New Canvas"
            enabled={canvasEnabled}
          />
        )}

        {/* 5. V2MOM — hidden when workspace module is disabled */}
        {v2momEnabled && (
          <StrategyTypeCard
            accent="indigo"
            icon={AlignVerticalJustifyStart}
            name="V2MOM"
            tagline="One page per leader, aligned top to bottom"
            description="Vision, Values, Methods, Obstacles, Measures. Every leader writes theirs against the one above it, so the whole organization reads as one chain from the CEO down."
            objectChain={v2momChain}
            count={v2moms.length}
            countLabel={v2moms.length === 1 ? 'V2MOM' : 'V2MOMs'}
            listHref="/prioritization/strategy/v2mom"
            newHref="/prioritization/strategy/v2mom"
            newLabel="New V2MOM"
            enabled={v2momEnabled}
          />
        )}
      </div>

      {/* Unified strategies table */}
      <AllStrategiesTable rows={allRows} />

      {/* OST Recommendations */}
      <OSTRecommendations />
    </div>
  );
}

export default StrategiesHub;
